﻿import { FilterGroup, TagBorderMode } from '@fe/core';
import { MyFleetAcidListItem } from '../components/my-fleet/types';

// Enums
export enum UDViewType {
    user,
    company
}

export enum UDViewMapStyle {
    dark = 0,
    light = 1,
    blue = 2,
    streets = 3,
    satellite = 4,
    terrainStreets = 5,
    terrainSatellite = 6,
    darkReversed = 7,
    hybrid = 8,
    classic = 9
}

export enum UDViewDayNightTwilightType {
    none = 0,
    sunrise = 1,
    civil = 2,
    nautical = 4,
    astronomical = 8
}

export enum UDViewDayNightShadingType {
    shade,
    twilight
}

export enum UDViewTerrainShadingType {
    singleColor,
    colorRamp
}

export enum UDViewTimeFilterTypeEnum {
    none,
    departureTime,
    arrivalTime
}

export enum UDViewTagTextColor {
    default = 0,
    sameAsPlane = 1
}

export enum UDViewTagSelectionEnum {
    None = 0,
    Acid = 1,
    Altitude = 2,
    Speed = 3,
    Origin = 4,
    Destination = 5,
    AircraftType = 6,
    DepTimeEtd = 7,
    EtaFaa = 8,
    EtaFe = 9,
    Scratchpad = 10,
    TailNumber = 11,
    Latitude = 12,
    Longitude = 13,
    DistanceToDestination = 14,
    EventText = 15,
    DestinationChange = 16,
    PositionSource = 17,
    LastDestination = 18,
    CtdEdct = 19,
    ControlElement = 20,
    Track = 21,
    DistanceOffRoute = 22,
    FuelOnBoard = 23,
    EtrFaa = 24,
    EtrFe = 25,
    OriginGate = 26,
    DestinationGate = 27,
    Priority = 28,
    NoseNumber = 29,
    AlternateDestination = 30,
    CTA = 31,
    InitialDelayReason = 32,
    LastDelayReason = 33,
    Status = 34,
    DepartureCenter = 35,
    ArrivalCenter = 36,
    OETD = 37,
    OETA = 38,
    OETE = 39,
    ETE = 40,
    ControlDepDel = 41,
    NonCtlDepDel = 42,
    ControlArrivalDel = 43,
    NonCtlArrivalDel = 44,
    EtedEvFromO = 45,
    IrregularOperations = 46,
    RouteText = 47,
    FuelReserve = 48,
    PETD = 49,
    PETA = 50,
    PETE = 51,
    CETE = 52,
    Impacts = 53,
    SectordeskId = 54,
    Out = 55,
    ElapsedSinceOut = 56,
    On = 57,
    In = 58,
    UserField2 = 59,
    Holding = 60,
    Diverting = 61,
    ImpactAFM = 62,
    SincePositionUpdate = 63,
    LastPositionTime = 64,
    Off = 65,
    AlternateDestination2 = 67,
    Divrecpri = 68,
    TOA = 69,
    Runway = 70,
    Pairedfixid = 71,
    NumSeats = 72,
    NumPassanger = 73,
    NumCrew = 74,
    CrewId = 75,
    MonitorStatus = 76,
    Beacon = 77,
    ModeS = 78,
    ObjectClass = 79,
    PlannedSpeed = 80,
    PlannedAltitude = 81,
    GoAround = 82,
    DevAltPlan = 83,
    DevFobPlan = 84,
    DepartureDev = 85,
    ArrivalDev = 86,
    // Don't modify values above, they must match with C# model
    TaxiTime = 87,
    Notes = 88,
    AlternateDestinationOther = 89,
    etopsAlts = 90,
    FuelOnBoardTime = 91
}

export enum UDViewPlaneFilterAirlineTypeEnum {
    SpecificAirlineId = 1,
    AllOfMyFleet = 2,
    AllGeneralAviation = 3
}

export enum UDViewFlightFilterObjectEnum {
    None,
    Aircraft,
    GroundVehicle,
    All
}

export enum UDViewDestinationTypeEnum {
    OriginToDestination,
    Destination,
    OriginOrDestination,
    DivertingFrom,
    DivertingTo,
    Alternate,
    BlankOriginOrDestination
}

export enum MyFleetMode {
    disabled = 0,
    airline = 1,
    airport = 2,
    acid = 3
}

export enum MyFleetAirportModeEnum {
    departureArrivals = 0,
    arrivals = 1,
    departures = 2
}

export enum MyFleetAcidListSource {
    manual = 0,
    desktopInterface = 1,
    file = 2
}

export enum UDViewTimeDistanceMode {
    lastNext,
    allRemaining
}

export enum UDViewHistoryColorSource {
    aircraft = 'aircraft',
    history = 'history'
}

export enum UDViewShowFlightPlanTimeEnum {
    o,
    t,
    tr
}

export enum UDViewPlaneIconSizeEnum {
    auto,
    small,
    medium,
    large,
    jumbo,
    extraLarge
}

export enum UDViewPlaneIconTypeEnum {
    auto,
    circle,
    triangle
}

export enum UDViewRangeType {
    time,
    distance,
    pixel,
    time389,
    timeCustomSpeed,
    distanceRLOS,
    distanceFobCurrentSpeed,
    distanceFobCustomSpeed
}

// Common
export interface UDViewCommonSettingsItem {
    key: string;
    value: string;
}

export interface UDViewFilterGroupModel {
    // The reason ids and config for each filter group is split into separate objects is because we want to preserve config when the filter group is no selected
    show: boolean;
    ids: string[];
    config: Partial<Record<string, UDViewFilterGroupSettingsModel>>;
}

export interface UDViewFilterGroupSettingsModel {
    color: string | null;
}

export interface UDViewStaticDataSubLayerWithColor {
    show: boolean;
    color: string | null;
}

// Plane
export interface UDViewPlaneModel {
    settings: UDViewPlaneSettingModel;
    filter: UDViewPlaneFilterListModel;
    icon: UDViewPlaneIconSettingModel;
    tag: UDViewPlaneTagModel;
    range: UDViewPlaneRangeModel;
    vector: UDViewPlaneVectorModel;
}

export interface UDViewPlaneSettingModel {
    show: boolean;
    showHistory: boolean;
    showActive: boolean;
    showParked: boolean;
    showScheduled: boolean;
    showPlanned: boolean;
    showDiversionLines: boolean;
    showGroundVehicles: boolean;
    historyColor: string | null;
    parkedColor: string | null;
    scheduledColor: string | null;
    plannedColor: string | null;
    flightPlanColor: string | null;
    oldFlightPlanColor: string | null;
    routeLabelsColor: string | null;
    routeFlownColor: string | null;
    flown3PdiColor: string | null;
    flownProjectionColor: string | null;
    showFlightPlanPointMark: boolean;
    showFlightPlanPointLabel: boolean;
    flightPlanExtent: boolean;
    showRouteFlownPointMark: boolean;
    showFlightPlanTime: UDViewShowFlightPlanTimeEnum;
    showProjectedPoints: boolean;
    showLastReal: boolean;
    showProjectedHighlight: boolean;
    projectedHighlightColor: string | null;
    enableCoastMode: boolean;
    historyModern: boolean;
    historyColorSource: UDViewHistoryColorSource;
    showCoastPosition: boolean;
    coastPositionColor: string | null;
    enableTimeDistance: boolean;
    udViewTimeDistanceMode: UDViewTimeDistanceMode;
    routeFlown: UDViewPlaneSettingRouteFlownModel;
    cityPair: UDViewCityPairModel;
}

export interface UDViewPlaneSettingRouteFlownPointConfigModel {
    color: string;
    enabled: boolean;
}

export interface UDViewPlaneSettingRouteFlownModel {
    defaultColor: string;
    projected: UDViewPlaneSettingRouteFlownPointConfigModel;
    acars: UDViewPlaneSettingRouteFlownPointConfigModel;
    adsbTerrestrial: UDViewPlaneSettingRouteFlownPointConfigModel;
    adsbSatellite: UDViewPlaneSettingRouteFlownPointConfigModel;
    faaAnsp: UDViewPlaneSettingRouteFlownPointConfigModel;
}

export interface UDViewCityPairModel {
    show: boolean;
    active: boolean;
    planned: boolean;
    scheduled: boolean;
    activeColor: string;
    plannedColor: string;
    scheduledColor: string;
}

export interface UDViewPlaneFilterListModel {
    useFilters: boolean;
    startWithNoPlanes: boolean;
    // Auto zoom
    planeFilterSettings: UDViewPlaneFilterSettingModel[];
}

export interface UDViewPlaneFilterSettingModel {
    name: string;
    color: string;
    isActive: boolean;
    include: boolean;
    airlineType: UDViewPlaneFilterAirlineTypeEnum;
    useMyFleet: boolean;
    acid: string | null;
    origin: string | null;
    destination: string | null;
    destinationType: UDViewDestinationTypeEnum;
    useAltAirport: boolean;
    beaconCode: string | null;
    icaoCode: string | null;
    altitudeLower: number | null;
    altitudeUpper: number | null;
    speedLower: number | null;
    speedUpper: number | null;
    aircraftType: string | null;
    filterObjectType: UDViewFlightFilterObjectEnum;
    timeBefore: number | null;
    timeAfter: number | null;
    timeType: UDViewTimeFilterTypeEnum | null;
    distanceToDestination: number | null;
    sectorDeskId: string | null;
    tail: string | null;
    diverted: boolean;
    holding: boolean;
    showTags: boolean;
    showHistory: boolean;
    showHolding: boolean;
    showDiverting: boolean;
    tagTextColor: UDViewTagTextColor;
}

export interface UDViewPlaneIconSettingModel {
    zoomLevel: number;
    size: UDViewPlaneIconSizeEnum;
    type: UDViewPlaneIconTypeEnum;
    drawShadow: boolean;
}
export interface UdViewPlaneSettingsModel {
    show: boolean;
}

export interface UDViewPlaneTagModel {
    show: boolean;
    labels: UDViewPlaneTagLabelModel[];
    zoomLevel: number;
    disableTagLeader: boolean;
    enableAutoTag: boolean;
    tagShowMyNotesIcon: boolean;
    hideAirlineId: boolean;
    showLabelBackground: boolean;
    backgroundColor: string | null;
    textColor: string | null;
    outlineMode: TagBorderMode;
    outlineColor: string | null;
}

export interface UDViewPlaneTagLabelModel {
    type: UDViewTagSelectionEnum;
    label: string | null;
    enabled: boolean;
}

export interface UDViewPlaneRangeModel {
    isVisible: boolean;
    factor: number;
    type: UDViewRangeType;
    color: string | null;
    lineColor: string | null;
}

export interface UDViewPlaneVectorModel {
    isVisible: boolean;
    factor: number;
    type: UDViewRangeType;
    lineColor: string | null;
}

// Weather

// Static data

export interface UDViewStaticDataModel {
    airport: UDViewAirportModel;
    airspace: UDViewAirspaceModel;
    airway: UDViewAirwayModel;
    drawingObjects: UDViewDrawingObjectModel;
    fixes: UDViewFixesModel;
    flightServiceStation: UDViewFlightServiceStationModel;
    geographyMap: UDViewGeographyMapModel;
    holding: UDViewHoldingModel;
    gridMora: UDViewGridMoraModel;
    latLon: UDViewLatLonModel;
    navaids: UDViewNavaidsModel;
    overwaterAreas: UDViewOverwaterAreasModel;
    miscellaneous: UDViewMiscellaneousModel;
    sids: UDViewSidsModel;
    stars: UDViewStarsModel;
    sua: UDViewSUAModel;
    timeZone: UDViewTimeZoneModel;
    weatherStation: UDViewWeatherStationModel;
    customGeojson: UDViewCustomGeojsonModel;
}

export interface UDViewGeojsonUrlPath {
    type: 'url';
    show: boolean;
    name: string;
    url: string;
}

export interface UDViewGeojsonFilePath {
    type: 'file';
    show: boolean;
    name: string;
    fileHandle: string;
}

export type UDViewGeojsonPath = UDViewGeojsonUrlPath | UDViewGeojsonFilePath;

export interface UDViewCustomGeojsonModel {
    show: boolean;
    paths: UDViewGeojsonPath[];
}

export interface UDViewAirportSettingsModel {
    fontSize: number;
    airportLabelType: 'faa' | 'icao' | 'iata' | 'iataIcao' | 'icaoIata';
    activeRunwayDepiction: boolean;
    departureColor: string;
    arrivalColor: string;
    directionIndicator: boolean;
    rangeRingsEnabled: boolean;
    rangeRingsLineColor: string;
    rangeRingsLineStyle: 'solid' | 'none';
    rangeRingsFillColor: string;
    rangeRingsFillStyle: 'solid' | 'none';
    rangeRingsOpacity: number;
    rangeRingsSingleRing: boolean;
    rangeRingsSingleRingDistance: number;
    rangeRingsMultipleRings: number;
    rangeRingsMultipleRingSpacing: number;
    applyToAllAirports: boolean;
    selectedGroupId: string | null;
    runwayLabels: boolean;
    runwayWindIndicators: boolean;
    // blend gust speed into wind indicator vector calculations
    runwayWindGust: boolean;
    runwayWindGustFactor: number;
    runwayRvrIndicators: boolean;
}
export interface UDViewAirportModel {
    show: boolean;
    myAirports: UDViewStaticDataSubLayerWithColor;
    tower: UDViewStaticDataSubLayerWithColor;
    noTower: UDViewStaticDataSubLayerWithColor;
    heliport: UDViewStaticDataSubLayerWithColor;
    seaplane: UDViewStaticDataSubLayerWithColor;
    others: UDViewStaticDataSubLayerWithColor;
    runways: boolean;
    deicing: boolean;
    filterGroup: UDViewFilterGroupModel;
    settings: UDViewAirportSettingsModel;
}

export interface UDViewAirspaceModel {
    show: boolean;
    fir: boolean;
    uir: boolean;
    classA: boolean;
    classB: boolean;
    classC: boolean;
    classD: boolean;
    classE: boolean;
    classF: boolean;
    classG: boolean;
    other: boolean;
    filterGroup: UDViewFilterGroupModel;
}

export interface UDViewAirwayModel {
    show: boolean;
    high: UDViewStaticDataSubLayerWithColor;
    low: UDViewStaticDataSubLayerWithColor;
    settings: {
        fontSize: number;
    };
    filterGroup: UDViewFilterGroupModel;
}

export interface UDViewDrawingObjectModel {
    show: boolean;
    avoidance: boolean;
    alert: boolean;
    view: boolean;
    viewObjectIds: string[];
}

export interface UDViewFixesModel {
    show: boolean;
    latLon: UDViewStaticDataSubLayerWithColor;
    nrs: UDViewStaticDataSubLayerWithColor;
    aacaA_SUA: UDViewStaticDataSubLayerWithColor;
    intersection: UDViewStaticDataSubLayerWithColor;
    rnav: UDViewStaticDataSubLayerWithColor;
    terminal: UDViewStaticDataSubLayerWithColor;
    vfr: UDViewStaticDataSubLayerWithColor;
    other: UDViewStaticDataSubLayerWithColor;
    low: boolean;
    high: boolean;
    settings: {
        fontSize: number;
    };
    filterGroup: UDViewFilterGroupModel;
}

export interface UDViewFlightServiceStationModel {
    show: boolean;
    filterGroup: UDViewFilterGroupModel;
}

export interface UDViewGeographyMapModel {
    drawDayNight: boolean;
}

export interface UDViewHoldingModel {
    show: boolean;
    lowAltitude: boolean;
    highAltitude: boolean;
    sid: boolean;
    star: boolean;
    approach: boolean;
    missedApproach: boolean;
    other: boolean;
    filterGroup: UDViewFilterGroupModel;
}

export interface UDViewGridMoraModel {
    show: boolean;
}

export interface UDViewLatLonModel {
    show: boolean;
}

export interface UDViewNavaidsModel {
    show: boolean;
    ndb: UDViewStaticDataSubLayerWithColor;
    vor: UDViewStaticDataSubLayerWithColor;
    voR_DME: UDViewStaticDataSubLayerWithColor;
    vortac: UDViewStaticDataSubLayerWithColor;
    tacan: UDViewStaticDataSubLayerWithColor;
    dme: UDViewStaticDataSubLayerWithColor;
    ndB_DME: UDViewStaticDataSubLayerWithColor;
    rings: {
        draw: boolean;
        drawTerminal: boolean;
        drawLowAltitude: boolean;
        drawHighAltitude: boolean;
        drawUnrestricted: boolean;
        fillColor: string;
        lineColor: string;
    };
    settings: {
        fontSize: number;
    };
    filterGroup: UDViewFilterGroupModel;
}

export interface UDViewOverwaterAreasModel {
    show: boolean;
    lineColor: string;
}

export interface UDViewMiscellaneousModel {
    backGroundBrightness: number;
}

export interface UDViewSidsModel {
    show: boolean;
    filterGroup: UDViewFilterGroupModel;
}
export interface UDViewStarsModel {
    show: boolean;
    filterGroup: UDViewFilterGroupModel;
}

export interface UDViewSUAModel {
    show: boolean;
    static: boolean;
    dynamic: boolean;
    alert: boolean;
    caution: boolean;
    danger: boolean;
    militaryArea: boolean;
    prohibited: boolean;
    restricted: boolean;
    traning: boolean;
    warning: boolean;
    unkown: boolean;
    filterGroup: UDViewFilterGroupModel;
}

export interface UDViewTimeZoneModel {
    show: boolean;
}

export interface UDViewWeatherStationModel {
    show: boolean;
    filterGroup: UDViewFilterGroupModel;
}
///

// Weather
export interface UDViewWeatherModel {
    show: boolean;
    all: number[];
    hidden: number[];
    settings: UDViewCommonSettingsItem[];
}

export type UDViewTerrainShadingModel = {
    enabled: boolean;
    type: UDViewTerrainShadingType;
    altitude: number;
    opacity: number;
    color: string;
};

// Map
export interface UDViewMapModel {
    dayNightShading: boolean;
    dayNightShadingType: UDViewDayNightShadingType;
    selectedTwilightTypes: UDViewDayNightTwilightType;
    terrainShading: UDViewTerrainShadingModel;
    lat: number;
    lon: number;
    pitch: number;
    bearing: number;
    zoom: number;
    terrain: boolean;
    style: UDViewMapStyle;
    useGlobeView: boolean;
    globeAutoRotate: boolean;
    globeAutoRotateSpeed: number;
}

export interface UDViewMapSkyModel {
    skyColor: string | null;
    /** @format double */
    skyHorizonBlend: number;
    horizonColor: string | null;
    /** @format double */
    horizonFogBlend: number;
    fogColor: string | null;
    /** @format double */
    fogGroundBlend: number;
    /** @format double */
    atmosphereBlend: number;
}

// UDView
export interface UdViewModel {
    currentFEWebVersion: string | null;
    currentDbFEWebVersion: number | null;
    originalFEWebVersion: string | null;
    originalDbFEWebVersion: number | null;
    name: string;
    /** @format uuid */
    id: string;
    /** @format date-time */
    lastUpdate: string | null;
    deleted: boolean;
    type: UDViewType;
    includeInSlideshow: boolean;
    isFavorite: boolean;
    plane: UDViewPlaneModel;
    weather: UDViewWeatherModel;
    staticData: UDViewStaticDataModel;
    map: UDViewMapModel;
}

// --- Other

interface MyFleetDisabled {
    mode: MyFleetMode.disabled;
}

interface MyFleetCommon {
    deskFilter: string[];
}

interface MyFleetAirlinesMode {
    mode: MyFleetMode.airline;
    airlines: string[];
    applyFlightNumberPattern: boolean;
}

interface MyFleetAirportMode {
    mode: MyFleetMode.airport;
    airport: string | null;
    airportMode: MyFleetAirportModeEnum;
}

interface MyFleetAcidMode {
    mode: MyFleetMode.acid;
    acids: MyFleetAcidListItem[];
}

export type MyFleetSettings = MyFleetDisabled | (MyFleetCommon & (MyFleetAirlinesMode | MyFleetAirportMode | MyFleetAcidMode));

export interface MyAirportSettings {
    filterGroup?: FilterGroup;
    includeOrgDestAlt: boolean;
    includeScheduledMyFleet: boolean;
}

export type UdViewWeatherSettings = UDViewCommonSettingsItem[];

export interface AlwaysLoadWeatherItem {
    id: number;
    record: boolean;
}
